import { Component, Inject, OnDestroy } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
	LO_DIALOG_DATA,
	LoButton,
	LoDialog,
	LoDialogRef,
	LoNotice,
	LoNumberField,
	LoToggle
} from 'lo-design-system';
import { Subject, takeUntil } from 'rxjs';
import { AutoRechargeService } from '@brand-lolly/app/billing/services/auto-recharge.service';
import { OpenBillingPortalSessionService } from '@brand-lolly/app/billing/services/open-billing-portal-session.service';
import {
	AUTO_TOP_UP_CAP_PHRASE,
	pausedReasonPhrase
} from '@brand-lolly/app/billing/types/card-decline.types';
import { BillingErrorsService } from '@brand-lolly/app/billing/services/billing-errors.service';
import { AutoRechargeSaveRequest } from '@brand-lolly/app/billing/types/mutations.types';
import {
	AutoTopUpDialogData,
	AutoTopUpDialogResult
} from './auto-top-up-dialog.model';
import { AnaChannelConnectService } from '@brand-lolly/app/_services/ana-channel-connect.service';

/** Mirrors the backend floor/ceiling (CUSTOM_MIN_CENTS / CUSTOM_MAX_CENTS in
 *  app/billing/credits/packs.py: $100 and $5,000). */
const MIN_AMOUNT_USD = 100;
const MAX_AMOUNT_USD = 5000;

@Component({
	standalone: true,
	selector: 'app-auto-top-up-dialog',
	templateUrl: './auto-top-up-dialog.component.html',
	styleUrls: ['./auto-top-up-dialog.component.scss'],
	imports: [
		ReactiveFormsModule,
		LoButton,
		LoDialog,
		LoNotice,
		LoNumberField,
		LoToggle
	]
})
export class AutoTopUpDialogComponent implements OnDestroy {
	readonly minAmountUsd = MIN_AMOUNT_USD;
	readonly maxAmountUsd = MAX_AMOUNT_USD;
	/** Sentence case: the hint below the toggle opens with it. */
	readonly capNote = `Charged to your saved card, ${AUTO_TOP_UP_CAP_PHRASE}. Past that it pauses itself and tells you.`;

	enabled = false;
	thresholdControl = new FormControl<number | null>(null);
	amountControl = new FormControl<number | null>(null);
	/**
	 * True while the save request is out. The two fields are locked with it
	 * through their controls: a lo field is disabled by its form control, not
	 * by an input beside it.
	 */
	get saving(): boolean {
		return this.inFlight;
	}
	set saving(value: boolean) {
		this.inFlight = value;
		for (const control of [this.thresholdControl, this.amountControl]) {
			if (value) {
				control.disable({ emitEvent: false });
			} else {
				control.enable({ emitEvent: false });
			}
		}
	}
	private inFlight = false;
	portalOpening = false;

	private unsubscriber = new Subject<void>();

	constructor(
		@Inject(LO_DIALOG_DATA) public data: AutoTopUpDialogData,
		private dialogRef: LoDialogRef<
			AutoTopUpDialogComponent,
			AutoTopUpDialogResult
		>,
		private autoRechargeService: AutoRechargeService,
		private portalService: OpenBillingPortalSessionService,
		private billingErrors: BillingErrorsService,
		private anaConnect: AnaChannelConnectService
	) {
		const settings = data.settings;
		this.enabled = settings.enabled;
		this.thresholdControl.setValue(settings.threshold_credits || null);
		this.amountControl.setValue(
			settings.topup_amount_cents
				? settings.topup_amount_cents / 100
				: null
		);
	}

	ngOnDestroy(): void {
		this.unsubscriber.next();
		this.unsubscriber.complete();
	}

	get hasSavedCard(): boolean {
		return this.data.settings.has_saved_card;
	}

	get thresholdCredits(): number | null {
		return this.toNumber(this.thresholdControl.value);
	}

	get amountUsd(): number | null {
		return this.toNumber(this.amountControl.value);
	}

	/** Credits one top-up buys — preview only; the server recomputes it. */
	get topUpCredits(): number | null {
		const amount = this.amountUsd;
		const rate = this.data.creditsPerUsd;
		if (amount === null || rate === null) {
			return null;
		}
		return Math.round(amount * rate);
	}

	/** The credits one top-up buys, shown under the amount while it is acceptable. */
	get amountHint(): string {
		if (this.topUpCredits === null || this.amountOutOfRange) {
			return '';
		}
		return `≈ ${this.topUpCredits.toLocaleString(
			'en-US'
		)} credits per top-up`;
	}

	get amountError(): string {
		if (this.amountOutOfRange) {
			return `Enter an amount between $${MIN_AMOUNT_USD.toLocaleString(
				'en-US'
			)} and $${MAX_AMOUNT_USD.toLocaleString('en-US')}.`;
		}
		if (this.amountBelowThreshold) {
			return 'A top-up must add more credits than the threshold, otherwise it would trigger again immediately.';
		}
		return '';
	}

	get amountOutOfRange(): boolean {
		const amount = this.amountUsd;
		if (amount === null) {
			return false;
		}
		return amount < MIN_AMOUNT_USD || amount > MAX_AMOUNT_USD;
	}

	/**
	 * The backend rejects a config whose top-up can't lift the balance back
	 * above the threshold (it would re-fire immediately). Surface it up front.
	 */
	get amountBelowThreshold(): boolean {
		const credits = this.topUpCredits;
		const threshold = this.thresholdCredits;
		if (credits === null || threshold === null) {
			return false;
		}
		return credits <= threshold;
	}

	get canSave(): boolean {
		if (this.saving || this.portalOpening) {
			return false;
		}
		if (!this.enabled) {
			// Turning it off needs no valid threshold/amount — but there is
			// nothing to save if it is already off (that state used to leave
			// Save clickable and fail on the empty amount).
			return this.data.settings.enabled;
		}
		if (!this.hasSavedCard) {
			return false;
		}
		const threshold = this.thresholdCredits;
		const amount = this.amountUsd;
		return (
			threshold !== null &&
			threshold >= 0 &&
			amount !== null &&
			!this.amountOutOfRange &&
			!this.amountBelowThreshold
		);
	}

	/**
	 * Why automatic top-up is paused, as a sentence rather than the provider's
	 * own token — see `card-decline.types.ts`.
	 */
	pausedReason(reason: string | null): string | null {
		return pausedReasonPhrase(reason);
	}

	/**
	 * Cards live in Stripe, not in our UI — send the user straight to the
	 * Customer Portal instead of telling them to find it. Returning to this
	 * page re-reads the settings, so the toggle unlocks once a card exists.
	 *
	 * The session asks for the `payment_method_update` flow so it opens the
	 * add-card form itself; a bare session lands on the portal overview, which
	 * has no add-card form on it at all (T-1707).
	 */
	addPaymentMethod(): void {
		if (this.portalOpening) {
			return;
		}
		this.portalOpening = true;
		this.portalService
			.open({
				return_url: window.location.href,
				flow: 'payment_method_update'
			})
			.pipe(takeUntil(this.unsubscriber))
			.subscribe({
				next: response => {
					if (response.url) {
						this.anaConnect.redirectExternally(
							response.url,
							'billing'
						);
						return;
					}
					this.portalOpening = false;
				},
				error: error => {
					this.portalOpening = false;
					this.billingErrors.handle(
						error,
						'Could not open the billing portal.'
					);
				}
			});
	}

	onToggle(checked: boolean): void {
		this.enabled = checked;
	}

	dismiss(): void {
		this.dialogRef.close({ settings: null });
	}

	save(): void {
		if (!this.canSave) {
			return;
		}
		const request: AutoRechargeSaveRequest = {
			enabled: this.enabled,
			threshold_credits: this.thresholdCredits ?? 0,
			topup_amount_cents: Math.round((this.amountUsd ?? 0) * 100)
		};
		this.saving = true;
		this.autoRechargeService
			.save(request)
			.pipe(takeUntil(this.unsubscriber))
			.subscribe({
				next: settings => {
					this.saving = false;
					this.dialogRef.close({ settings });
				},
				error: error => {
					this.saving = false;
					this.billingErrors.handle(
						error,
						'Could not update auto top-up.'
					);
				}
			});
	}

	private toNumber(raw: number | null): number | null {
		if (raw === null) {
			return null;
		}
		const parsed = Number(raw);
		return isNaN(parsed) ? null : parsed;
	}
}
